import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { convertTrial } from 'slices/authSlice'
import routes from 'constants/routes'
import { Loader2 } from 'lucide-react'

/**
 * Trial conversion. Reachable voluntarily (from the countdown banner) or as the
 * lockout screen once the trial has expired. Collects a password, promotes the
 * account to a real (community) account, and returns the user to the app.
 * Their seeded data is kept as-is.
 */
const ConvertAccountScreen = () => {
	const dispatch = useDispatch<any>()
	const navigate = useNavigate()
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)

	const [password, setPassword] = useState('')
	const [confirm, setConfirm] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [submitting, setSubmitting] = useState(false)

	const isTrial = userInfo?.accountType === 'trial'
	const expired = isTrial && userInfo?.trialExpiresAt && new Date(userInfo.trialExpiresAt).getTime() < Date.now()

	// Non-trial users don't belong here — send them home.
	if (!isTrial) {
		navigate(routes.HOME, { replace: true })
		return null
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setError(null)
		if (password.length < 8) {
			setError('Please choose a password of at least 8 characters.')
			return
		}
		if (password !== confirm) {
			setError('Passwords do not match.')
			return
		}
		setSubmitting(true)
		try {
			await dispatch(convertTrial({ password })).unwrap()
			navigate(routes.HOME, { replace: true })
		} catch (err: any) {
			setError(typeof err === 'string' ? err : 'Something went wrong. Please try again.')
			setSubmitting(false)
		}
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
			<div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
				<h1 className="mb-1 text-xl font-bold text-gray-900">
					{expired ? 'Your preview has ended' : 'Create your account'}
				</h1>
				<p className="mb-6 text-sm text-gray-600">
					{expired
						? 'Set a password to keep your workspace and everything in it. Your data stays exactly as it is.'
						: 'Ready to make it yours? Set a password to convert your preview into a full account. Your data stays exactly as it is.'}
				</p>

				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-2">
						<label htmlFor="password" className="text-sm font-medium text-gray-700">
							Password
						</label>
						<input
							id="password"
							type="password"
							autoComplete="new-password"
							value={password}
							onChange={e => setPassword(e.target.value)}
							className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
							placeholder="At least 8 characters"
						/>
					</div>
					<div className="space-y-2">
						<label htmlFor="confirm" className="text-sm font-medium text-gray-700">
							Confirm password
						</label>
						<input
							id="confirm"
							type="password"
							autoComplete="new-password"
							value={confirm}
							onChange={e => setConfirm(e.target.value)}
							className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
							placeholder="Re-enter your password"
						/>
					</div>

					{error && <p className="text-sm text-destructive">{error}</p>}

					<button
						type="submit"
						disabled={submitting}
						className="inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-60"
					>
						{submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						Create my account
					</button>
				</form>
			</div>
		</div>
	)
}

export default ConvertAccountScreen
